import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AssessmentInfo } from "@/types/assessment";
import { Course } from "@/types/course";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";

interface ImportProgress {
  current: number;
  total: number;
  currentFileName: string;
  isImporting: boolean;
}

interface ImportSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (assessmentId: string | null) => Promise<void>;
  selectedFiles: File[];
  progress: ImportProgress;
  assessments: AssessmentInfo[];
  courses: Course[];
  error: string | null;
}

export const ImportSettingsDialog = ({
  isOpen,
  onClose,
  onImport,
  selectedFiles,
  progress,
  assessments,
  courses,
  error,
}: ImportSettingsDialogProps) => {
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | null
  >(null);

  // Reset values when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedAssessmentId(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await onImport(selectedAssessmentId);
  };

  // Group assessments by course
  const groupedAssessments = courses.reduce((acc, course) => {
    const courseAssessments = assessments.filter(
      (assessment) => 
        assessment.courseId === course.id &&
        assessment.type === "written" &&
        !assessment.isFormative
    );
    if (courseAssessments.length > 0) {
      acc[course.id] = {
        course,
        assessments: courseAssessments,
      };  
    }
    return acc;
  }, {} as Record<string, { course: Course; assessments: AssessmentInfo[] }>);

  const getProgressPercentage = () => {
    if (progress.total === 0) return 0;
    return (progress.current / progress.total) * 100;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-[500px]!"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {selectedFiles.length === 1 ? "Upload submission" : "Batch import"}
          </DialogTitle>
          <DialogDescription>
            You need to include student IDs in the file name when uploading submissions manually.
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        )}
        
        {!progress.isImporting && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assessment">Assessment</Label>
              <Select
                value={selectedAssessmentId || undefined}
                onValueChange={setSelectedAssessmentId}
              >
                <SelectTrigger id="assessment" className="w-full truncate max-w-[450px]">
                  <SelectValue placeholder="Select an assessment" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(groupedAssessments).map(({ course, assessments }) => (
                    <SelectGroup key={course.id}>
                      <SelectLabel>{course.title}</SelectLabel>
                      {assessments.map((assessment) => (
                        <SelectItem key={assessment.id} value={assessment.id}>
                          {assessment.title}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                Import {selectedFiles.length === 1 ? "file" : `${selectedFiles.length} files`}
              </Button>
            </div>
          </form>
        )}

        {progress.isImporting && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading documents...</span>
                <span>{progress.current}/{progress.total}</span>
              </div>
              <Progress value={getProgressPercentage()} className="w-full" />
            </div>
            
            {progress.currentFileName && (
              <div className="text-sm text-gray-600">
                Current: {progress.currentFileName}
              </div>
            )}
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing...
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
